import React, { useContext, useMemo } from "react";
import { Checkbox } from "@foris/avocado-suite";
import { AppContext } from "../../context/EditSessionsContext";
import { Session, Section } from "@models/ISchema";
import { Types, EditedSession } from "../../context/formData.reducer";
import { IContextApp, ContextApp } from "@config/Context/contextApp";
import { useHiddenPastSessionIds } from "../../hooks/useHiddenPastSessionIds";
import SectionsList from "../sections-list";
import css from "./accordion.module.scss";
import { useTranslation } from "react-i18next";

interface AccordionSectionsProps {
  onCompareBySession: (value: Session) => void;
  onCompareBySection: (
    sessions: Session[],
    section: Section,
    checked: boolean,
    newSessions: EditedSession[],
  ) => boolean;
}

const AccordionSections = (props: AccordionSectionsProps) => {
  const { state, dispatch } = useContext(AppContext);
  const contextApp: IContextApp = useContext(ContextApp);
  const { t } = useTranslation();

  const userCanCreate =
    contextApp?.user?.permissions?.create &&
    contextApp?.user?.abilities?.can_edit_links_and_sections &&
    contextApp?.user?.abilities?.can_edit_or_delete_sessions;

  const orderedSections = useMemo(() => {
    if (!state?.link?.sections?.length) return null;

    return [...state.link.sections].sort((a, b) => {
      const codeA = a.component?.code ?? "";
      const codeB = b.component?.code ?? "";
      return codeA.localeCompare(codeB);
    });
  }, [state?.link?.sections]);

  const hiddenSessionIds = useHiddenPastSessionIds(orderedSections ?? []);

  const hasAllSessionsBySectionSelected = useMemo(() => {
    if (!state?.form?.selectedSessions?.length || !orderedSections?.length) return false;

    const selectedSessionsIds = [...(state?.form?.selectedSessions ?? [])].map(
      session => session.id,
    );

    return orderedSections.every(section => {
      const sectionSessions = [
        ...(section.sessions ?? []),
        ...(section.unasignedSessions ?? []),
      ].filter(session => !hiddenSessionIds.has(session.id));

      return sectionSessions.every(session => selectedSessionsIds.includes(session.id));
    });
  }, [state?.form?.selectedSessions, orderedSections, hiddenSessionIds]);

  const handleAllSessionsCheckedClick = () => {
    dispatch({
      type: Types.ToggleAllSessionsSelection,
      payload: {
        checked: !hasAllSessionsBySectionSelected,
        sections: orderedSections,
        hiddenSessionIds: Array.from(hiddenSessionIds),
      },
    });
  };

  if (!orderedSections?.length) return <></>;

  return (
    <div>
      {(orderedSections?.length ?? 0) > 1 && (
        <Checkbox
          className={css.selectAllBtn}
          labelRight={t("assignation-edit.sessions-list.select-all")}
          checked={hasAllSessionsBySectionSelected}
          onChange={handleAllSessionsCheckedClick}
        />
      )}

      <SectionsList
        sections={orderedSections}
        onCompareBySession={props.onCompareBySession}
        onCompareBySection={props.onCompareBySection}
        userCanCreate={userCanCreate}
      />
    </div>
  );
};

export default AccordionSections;
